import { SupportWidget } from "../../components/SupportWidget";
import { SupportLauncherProvider } from "../../components/support-launcher-visibility";
import { SampleDataBanner } from "../onboarding/SampleDataBanner";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { BannerSlot, Icon, Row, SidebarLayout } from "@wato/ui";
import { isApiError } from "../../api/client";
import { useQuery } from "@tanstack/react-query";
import { SHELL_STALE_MS } from "../../api/query-client";
import { useApi, useErrorMessage } from "../../api/context";
import type { Workspace, WorkspaceSummary } from "../../api/types";
import { useAuth } from "../../auth/AuthProvider";
import { SETUP_ROUTE } from "../../auth/guards";
import { PageError, PageLoading } from "../../components/common";
import { ReAcceptanceGate } from "../../components/ReAcceptanceGate";
import { NotFoundPage } from "../../components/NotFoundPage";
import { IconButton } from "../../components/IconButton";
import { usePageTitle } from "../../lib/usePageTitle";
import { useClearAppBadge } from "../../pwa/useClearAppBadge";
import { RealtimeProvider, useRealtimeSubscription } from "../../lib/realtime";
import { useIsMobile } from "../inbox/useIsMobile";
import { BottomNav } from "../mobile-shell/BottomNav";
import { InstallPrompt } from "../mobile-shell/InstallPrompt";
import {
  AutomationPauseProvider,
  AutomationStatusChip,
  MasterSwitch,
} from "../automation-pause/MasterSwitch";
import { AnnouncementBanners } from "../announcements/AnnouncementBanner";
import { ChannelHealthBanner } from "../channels/health-banner/ChannelHealthBanner";
import { PendingDeletionBanner } from "../settings/workspace/PendingDeletionBanner";
import { useGatedFeatures } from "../billing/useEntitlement";
import { NotificationBell } from "../notifications/NotificationBell";
import { rememberWorkspaceId } from "../workspace-switcher/last-workspace";
import { WorkspaceSwitcher } from "../workspace-switcher/WorkspaceSwitcher";
import { GlobalCommandPalette } from "../command-palette/GlobalCommandPalette";
import { useFeaturesWithData } from "./useFeaturesWithData";
import { useProspectorEnabled } from "../prospector/useProspectorEnabled";
import { mayUse } from "../roles/role-label";
import {
  filterNav,
  isConversationsOnlyStaff,
  NAV_GROUPS,
  NAV_TOP,
  staffMayOpen,
  topItemForPath,
} from "./nav-config";
import { SectionTabs } from "./SectionTabs";
import { AvatarMenu } from "./HeaderMenus";
import { SidebarFooter } from "./sidebar/SidebarFooter";
import { SidebarTop } from "./sidebar/SidebarTop";
import { applyNavMode, NavModeToggle, useNavMode } from "./nav-mode";
import { SidebarNav } from "./SidebarNav";
import { useSidebarCollapsed } from "./sidebar-collapse";
import { WorkspaceContext } from "./WorkspaceContext";
import { useShowAll } from "./show-all";
import { SupportBannerGate } from "./SupportBanner";
import { getSupportToken } from "./support-session";
import { PresenceHeartbeat } from "./PresenceHeartbeat";
import {
  OwnerAssistantProvider,
  useOwnerAssistant,
} from "../owner-assistant/OwnerAssistantProvider";
import { OwnerAssistantPanel } from "../owner-assistant/OwnerAssistantPanel";
import { OwnerAssistantFab } from "../owner-assistant/OwnerAssistantLauncher";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; workspace: Workspace; workspaces: readonly WorkspaceSummary[] }
  | { status: "not-found" }
  | { status: "setup-required" }
  | { status: "error"; error: unknown };

/**
 * `/w/:workspaceId/*` — header (workspace switcher, search, automation dot, notifications, help, language, avatar menu) + sidebar. The
 * workspace id comes from the URL and is checked by the API on every call
 * (`requireWorkspaceAccess`), so an id the user is not a member of shows "not found".
 */
function WorkspaceShellInner() {
  const api = useApi();
  const errorMessage = useErrorMessage();
  const { workspaceId = "" } = useParams();
  const [state, setState] = useState<LoadState>({ status: "loading" });

  const load = useCallback(async () => {
    try {
      const [loaded, workspaces, modules] = await Promise.all([
        api.getWorkspace(workspaceId),
        // Support sessions reach one workspace only: the account list is not available to them.
        getSupportToken()
          ? Promise.resolve([] as readonly WorkspaceSummary[])
          : api.listWorkspaces(),
        // b80: module flags are best-effort; without them nothing is gated (legacy behaviour).
        api.getWorkspaceModules(workspaceId).catch(() => null),
      ]);
      const workspace: Workspace = modules ? { ...loaded, modules: modules.enabled } : loaded;
      rememberWorkspaceId(workspace.id);
      setState({ status: "ready", workspace, workspaces });
    } catch (error) {
      if (isApiError(error) && error.code === "AUTH_WORKSPACE_NOT_FOUND") {
        setState({ status: "not-found" });
      } else if (isApiError(error) && error.code === "AUTH_2FA_SETUP_REQUIRED") {
        setState({ status: "setup-required" });
      } else {
        setState({ status: "error", error });
      }
    }
  }, [api, workspaceId]);

  useEffect(() => {
    setState({ status: "loading" });
    void load();
  }, [load]);

  const context = useMemo(
    () =>
      state.status === "ready"
        ? {
            workspace: state.workspace,
            workspaces: state.workspaces,
            setWorkspace: (workspace: Workspace) =>
              setState({
                status: "ready",
                workspace,
                workspaces: state.workspaces.map((w) =>
                  w.id === workspace.id
                    ? { ...w, name: workspace.name, deletion: workspace.deletion ?? null }
                    : w,
                ),
              }),
            reload: load,
          }
        : null,
    [state, load],
  );

  if (state.status === "loading") return <PageLoading />;
  if (state.status === "not-found") return <NotFoundPage />;
  if (state.status === "setup-required") return <Navigate to={SETUP_ROUTE} replace />;
  if (state.status === "error" || !context) {
    return (
      <div className="mx-auto max-w-[var(--size-content)] px-6 py-10">
        <PageError
          message={state.status === "error" ? errorMessage(state.error) : undefined}
          onRetry={() => void load()}
        />
      </div>
    );
  }

  const { workspace } = context;
  return (
    <WorkspaceContext.Provider value={context}>
      <RealtimeProvider>
        <WorkspaceRealtimeSubscription workspaceId={workspace.id} />
        <PresenceHeartbeat workspaceId={workspace.id} />
        <OwnerAssistantProvider>
          <WorkspaceShellBody workspace={workspace} context={context} />
        </OwnerAssistantProvider>
      </RealtimeProvider>
    </WorkspaceContext.Provider>
  );
}

/** Auto-subscribes the realtime client to the active workspace's channel — WP-124 build step
 * 10 ("auto-subscribe to the active workspace's channel"); resubscribed automatically by
 * `RealtimeClient` on every reconnect. A separate component so it can call the hook, which
 * needs `<RealtimeProvider>` as an ancestor (mounted just above, in the same render). */
function WorkspaceRealtimeSubscription({ workspaceId }: { workspaceId: string }) {
  useRealtimeSubscription(workspaceId);
  return null;
}

function WorkspaceShellBody({
  workspace,
  context,
}: {
  workspace: Workspace;
  context: { workspaces: readonly WorkspaceSummary[] };
}) {
  const { t } = useI18n();
  const api = useApi();
  useClearAppBadge();
  const gatedFeatures = useGatedFeatures(workspace.id);
  // UX-15: skip the catalog call entirely when the plan lacks the catalog manager.
  const catalogQuery = useQuery({
    queryKey: ["catalog-availability", workspace.id],
    queryFn: () => api.getCatalogAvailability(workspace.id),
    enabled: gatedFeatures["catalog.manager"] !== false,
    staleTime: SHELL_STALE_MS,
  });
  // FB2-05: the catalog entry never disappears; a failed lookup locks it. While the first lookup
  // is in flight it renders unlocked so the sidebar does not flash a lock on every page load.
  const catalogAvailable = catalogQuery.isLoading || (catalogQuery.data ?? false);
  const { state: authState } = useAuth();
  const userId = authState.status === "ready" ? authState.me.user.id : "anon";
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const mobile = useIsMobile();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { setOpen: setAssistantOpen } = useOwnerAssistant();
  // OWN-317: the old /console route redirects to `?assistant=1`; open the panel and drop the param.
  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get("assistant") !== "1") return;
    setAssistantOpen(true);
    params.delete("assistant");
    const rest = params.toString();
    navigate({ pathname, search: rest ? `?${rest}` : "" }, { replace: true });
  }, [search, pathname, navigate, setAssistantOpen]);
  // Direction 1: the inbox opens with the 64px icon rail unless the user chose otherwise.
  const onInbox = /\/conversations(\/|$)/.test(pathname);
  const [sidebarCollapsed, toggleSidebar] = useSidebarCollapsed(userId, onInbox);
  const hasData = useFeaturesWithData(workspace.id);
  const showAll = useShowAll(workspace);
  // Flag-gated section: the probe only runs for members who may see its nav item.
  const prospectorEnabled =
    useProspectorEnabled(workspace.id, mayUse(workspace, "prospector.view")) === "on";
  const navGate = {
    catalogAvailable,
    gatedFeatures,
    granted: workspace.permissions.granted,
    businessType: showAll.businessType,
    modules: workspace.modules,
    hasData,
    prospectorEnabled,
  };
  const [navMode, setNavMode] = useNavMode(
    userId,
    authState.status === "ready" ? authState.me.user.navMode : undefined,
    (m) => api.updateProfile({ navMode: m }),
  );
  const nav = applyNavMode(filterNav(navGate), navMode);
  const visibleNav = [...nav.top, ...Object.values(nav.sections).flat()];
  // WP-125: a short, fixed subset of the same route table for the mobile bottom nav — the
  // full NAV (incl. every settings sub-page) doesn't fit a 360px-wide bottom bar.
  // OWN-219: three links + the assistant + «More» (the drawer lists everything else, settings too).
  const bottomNavKeys = new Set(["", "conversations", "contacts"]);
  const bottomNavItems = visibleNav
    .filter(
      (item, i, all) => bottomNavKeys.has(item.to) && all.findIndex((o) => o.to === item.to) === i,
    )
    .map((item) => ({
      to: item.to === "" ? `/w/${workspace.id}` : `/w/${workspace.id}/${item.to}`,
      label: item.label,
      icon: item.to,
      end: item.end ?? false,
    }));
  // UX-18: tab title from the active nav entry; an sr-only h1 only when the page has none.
  // App-like pages (inbox, flow canvas, pipeline) need the full screen; reading pages cap the
  // content column at 72rem. The header and sidebar always span the full window.
  const contentWidth = /\/(conversations|flows|pipeline)(\/|$)/.test(pathname)
    ? "max-w-[var(--size-content-wide)]"
    : "max-w-[var(--size-content)]";
  const base = `/w/${workspace.id}`;
  const active = [...NAV_TOP, ...NAV_GROUPS.flatMap((g) => g.items)]
    .filter((item) => {
      const full = item.to === "" ? base : `${base}/${item.to}`;
      return pathname === full || (!item.end && pathname.startsWith(`${full}/`));
    })
    .sort((a, b) => b.to.length - a.to.length)[0];
  // FB3-20: the «channel silent» banner only on Home and Channels.
  const showChannelBanner = pathname === base || pathname === `${base}/channels`;
  const rel = pathname.startsWith(base) ? pathname.slice(base.length).replace(/^\/|\/$/g, "") : "";
  const sectionTop = topItemForPath(rel);
  const pageName = active ? t(active.label) : null;
  usePageTitle(pageName, workspace.name, t("dashboard.title"));
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);
  const contentRef = useRef<HTMLElement>(null);
  const [pageHasH1, setPageHasH1] = useState(false);
  // A page that left the nav (so has no nav label) still gets its h1 from its own header title.
  const [headerTitle, setHeaderTitle] = useState<string | null>(null);
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const check = () => {
      setPageHasH1(el.querySelector("h1:not([data-shell-title])") !== null);
      setHeaderTitle(el.querySelector("[data-page-header] h2")?.textContent?.trim() || null);
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(el, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  return (
    <AutomationPauseProvider>
      <SupportLauncherProvider>
        <div className="min-h-screen bg-surface-muted max-md:pb-16">
          <a
            href="#main-content"
            data-testid="skip-link"
            className="sr-only focus:not-sr-only focus:fixed focus:start-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-surface focus:px-4 focus:py-3 focus:text-fg focus:shadow-lg"
          >
            {t("dashboard.nav.skipToContent")}
          </a>
          <InstallPrompt workspaceId={workspace.id} />
          <GlobalCommandPalette
            workspaceId={workspace.id}
            gate={navGate}
            open={paletteOpen}
            onOpenChange={setPaletteOpen}
          />
          {mobile ? (
            <header className="bg-surface-muted">
              <Row gap={2} justify="between" className="h-11 flex-nowrap px-4">
                <IconButton
                  variant="ghost"
                  aria-expanded={drawerOpen}
                  label={drawerOpen ? t("dashboard.nav.closeMenu") : t("dashboard.nav.openMenu")}
                  icon={<Icon name={drawerOpen ? "close" : "menu"} />}
                  onClick={() => setDrawerOpen((v) => !v)}
                />
                <Row gap={1} className="flex-nowrap items-center" data-testid="header-utilities">
                  <NotificationBell workspaceId={workspace.id} />
                  <AvatarMenu workspaceId={workspace.id} />
                </Row>
              </Row>
            </header>
          ) : null}

          <SidebarLayout
            collapsed={sidebarCollapsed}
            className="px-6 py-4 max-md:px-4 max-md:py-3"
            sidebar={
              mobile ? null : (
                <div className="sticky top-4 flex max-h-[calc(100dvh-2rem)] flex-col gap-2">
                  <SidebarTop
                    workspaceId={workspace.id}
                    workspaceName={workspace.name}
                    workspaces={context.workspaces}
                    collapsed={sidebarCollapsed}
                    onSearch={() => setPaletteOpen(true)}
                  />
                  <nav
                    aria-label={t("dashboard.nav.label")}
                    className="min-h-0 flex-1 overflow-y-auto rounded-2xl bg-app-rail p-2 shadow-sm"
                  >
                    <span
                      aria-hidden="true"
                      data-testid="rail-logo"
                      className={`mb-3 grid size-12 place-items-center rounded-xl bg-app-logo font-bold text-app-on-logo ${sidebarCollapsed ? "mx-auto" : "ms-1"}`}
                    >
                      W
                    </span>
                    <SidebarNav
                      workspaceId={workspace.id}
                      top={nav.top}
                      simplified={nav.simplified}
                      collapsed={sidebarCollapsed}
                    />
                    {sidebarCollapsed || nav.simplified ? null : (
                      <NavModeToggle mode={navMode} onChange={setNavMode} />
                    )}
                  </nav>
                  <SidebarFooter
                    workspaceId={workspace.id}
                    collapsed={sidebarCollapsed}
                    onToggle={toggleSidebar}
                  />
                </div>
              )
            }
          >
            {/* W15: reserve room for the floating support button (2.75rem + 1rem gap; on mobile also
                the 4rem bottom nav it floats above) so it never covers the last row/card. */}
            <div className={`mx-auto w-full ${contentWidth}`}>
              <main
                id="main-content"
                tabIndex={-1}
                ref={contentRef}
                className="pb-[calc(7.75rem+env(safe-area-inset-bottom))] outline-none md:pb-[4.5rem] [&_[data-testid=section-tabs]]:mb-4"
              >
                {!pageHasH1 && (pageName ?? headerTitle) && (
                  <h1 className="sr-only" data-shell-title>
                    {pageName ?? headerTitle} · {workspace.name}
                  </h1>
                )}
                {/* Workspace-wide notices first, then the section tabs, then the page itself, so a
                  notice never splits a page's tabs from its title. */}
                <div className="flex flex-col gap-2 empty:hidden [&:not(:empty)]:mb-3">
                  <MasterSwitch />
                  {/* G10: one banner at a time, in priority order (channel health > deletion >
                    announcements > sample data); the slot shows the first, the rest queue. */}
                  <BannerSlot>
                    {showChannelBanner ? <ChannelHealthBanner /> : null}
                    <PendingDeletionBanner />
                    <AnnouncementBanners workspaceId={workspace.id} />
                    <SampleDataBanner workspaceId={workspace.id} />
                  </BannerSlot>
                </div>
                {sectionTop && sectionTop.to !== "settings" ? (
                  <SectionTabs
                    workspaceId={workspace.id}
                    items={nav.sections[sectionTop.to] ?? []}
                    rel={rel}
                  />
                ) : null}
                <ReAcceptanceGate workspaceId={workspace.id}>
                  {isConversationsOnlyStaff(workspace.permissions.granted) && !staffMayOpen(rel) ? (
                    <Navigate to={`${base}/conversations`} replace />
                  ) : (
                    <Outlet />
                  )}
                </ReAcceptanceGate>
              </main>
            </div>
          </SidebarLayout>
          <SupportWidget workspaceId={workspace.id} />
          {drawerOpen ? (
            <nav
              aria-label={t("dashboard.nav.label")}
              className="fixed inset-x-0 top-0 bottom-16 z-40 overflow-y-auto bg-app-rail p-4 md:hidden"
            >
              <Row
                gap={2}
                className="mb-3 flex-wrap items-center border-b border-line pb-3"
                data-testid="drawer-utilities"
              >
                <WorkspaceSwitcher
                  currentId={workspace.id}
                  currentName={workspace.name}
                  workspaces={context.workspaces}
                  placement="start-down"
                />
                <IconButton
                  variant="ghost"
                  label={t("command.open")}
                  icon={<Icon name="search" size={18} />}
                  onClick={() => {
                    setDrawerOpen(false);
                    setPaletteOpen(true);
                  }}
                />
                <AutomationStatusChip compact />
              </Row>
              <SidebarNav
                workspaceId={workspace.id}
                top={nav.top}
                simplified={nav.simplified}
                onNavigate={() => setDrawerOpen(false)}
              />
            </nav>
          ) : null}
          <BottomNav
            items={bottomNavItems}
            onMore={() => setDrawerOpen((v) => !v)}
            moreOpen={drawerOpen}
          />
          <OwnerAssistantFab />
          <OwnerAssistantPanel />
        </div>
      </SupportLauncherProvider>
    </AutomationPauseProvider>
  );
}

/** ACT-13: the persistent support banner sits above the whole workspace shell. */
export function WorkspaceShell() {
  return (
    <>
      <SupportBannerGate />
      <WorkspaceShellInner />
    </>
  );
}
